import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import { ISearchSummaryRowResult } from '@models/ISearchSummaryRowResult';
import DidYouMeanResults from '@molecules/DidYouMeanResults/DidYouMeanResults';
import updateDataLayer from '@utils/helpers/gtm';
import SearchSummaryRow from './SearchSummaryRow';

interface Props {
  readonly hidePrices?: boolean;
  readonly searchSummaryResults: ISearchSummaryRowResult[];
  readonly searchValue: string;
  readonly productsStatusLabels: IProductListingStatusLabels;
  readonly resultsFoundLabel?: string;
  readonly listingPage?: string;
}

const SearchSummaryResults = ({
  hidePrices,
  listingPage,
  productsStatusLabels,
  resultsFoundLabel,
  searchSummaryResults,
  searchValue,
}: Props) => {
  const totalResultsFound = searchSummaryResults
    .map((summaryResult) => summaryResult.totalCount)
    .reduce((partialSum, totalCount) => partialSum + totalCount, 0);

  const searchSummaryWithDidYouMean = searchSummaryResults.find(
    (summaryResult) => summaryResult.didYouMeanResults
  );

  const handleDidYouMeanSearch = async (searchValue: string) => {
    updateDataLayer({
      event: 'searchBar',
      search_term: searchValue,
    });

    const convertedValue = searchValue
      .replace(/\+/g, '%2B') // allows + symbol to be used in search input
      .replace(/%(?![0-9A-Fa-f]{2})/g, '%25'); // allows standalone % symbol to be used in search input
    window.location.href = searchValue
      ? `${listingPage}?query=${convertedValue}`
      : `${listingPage}`;
  };

  return (
    <div data-testid="CT-48-Search-Summary-Results-Container">
      {resultsFoundLabel && (
        <p
          className="body-three container pt-5 text-grey-600 lg:pt-[70px]"
          data-testid="CT-48-Search-Summary-Results-Found-Label"
        >
          {resultsFoundLabel.replace('{0}', `${totalResultsFound}`)}
        </p>
      )}

      {!!searchSummaryWithDidYouMean?.didYouMeanResults?.suggestions
        ?.length && (
        <div className="container mt-5 lg:mt-10">
          <DidYouMeanResults
            searchCallback={handleDidYouMeanSearch}
            {...searchSummaryWithDidYouMean.didYouMeanResults}
          />
        </div>
      )}

      <div
        className={`component-padding pt-15 ${
          resultsFoundLabel ? 'lg:pt-[104px]' : 'lg:pt-[150px]'
        }`}
      >
        <div
          className="flex flex-col gap-20 lg:gap-[150px]"
          data-testid="CT-48-Search-Summary-Results-Row-Container"
        >
          {searchSummaryResults.map((summary) => (
            <SearchSummaryRow
              key={summary.title}
              {...summary}
              hidePrices={!!hidePrices}
              productsStatusLabels={productsStatusLabels}
              value={searchValue}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchSummaryResults;
